import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import { formatDateOnly } from '@/utils/DateOnlyHelpers'
import {
  BLOCKED_ORDER_COCKPIT_STATUSES,
  BLOCKED_ORDER_SAP_HANDOFF_STATUSES,
  isBlockedOrderErrorType,
  type BlockedOrderDecision,
  type BlockedOrderSource,
  type ProvinceCode,
} from '@repo/constants'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Blocked orders (CT-31), over /credit/blocked-orders.
//
// As elsewhere in the credit hooks, no `res.ok` check: the hono client throws the parsed error body
// on any non-2xx before `res.json()` is reached, and that body carries the `clr.errors.<key>` a
// toast is translated from. Response types are inferred from the router.
// ─────────────────────────────────────────────────────────────────────────────────────────────

const toDecisionBatch = <
  T extends { decidedAt: string; orders: { createdAtSap: string | null }[] },
>(
  batch: T,
) => ({
  ...(batch as Omit<T, 'decidedAt' | 'orders'>),
  decidedAt: new Date(batch.decidedAt),
  orders: batch.orders.map((order) => ({
    ...(order as Omit<T['orders'][number], 'createdAtSap'>),
    createdAtSap: order.createdAtSap ? new Date(order.createdAtSap) : null,
  })),
})

async function fetchBlockedOrderReview(caseId: string) {
  const res = await getApi().credit['blocked-orders'].reviews[':id'].$get({
    param: { id: caseId },
  })
  const detail = await res.json()

  // Timestamps cross the wire as ISO strings; every consumer wants Dates.
  return {
    ...detail,
    raisedAt: new Date(detail.raisedAt),
    resolvedAt: detail.resolvedAt ? new Date(detail.resolvedAt) : null,
    cutoffAt: detail.cutoffAt ? new Date(detail.cutoffAt) : null,
    order: {
      ...detail.order,
      documentDate: detail.order.documentDate ? new Date(detail.order.documentDate) : null,
      createdAtSap: detail.order.createdAtSap ? new Date(detail.order.createdAtSap) : null,
    },
    lastDecisionBatch: detail.lastDecisionBatch ? toDecisionBatch(detail.lastDecisionBatch) : null,
  }
}

export const blockedOrderReviewQueryOptions = (caseId: string) =>
  queryOptions({
    queryKey: ['blockedOrderReview', caseId],
    queryFn: () => fetchBlockedOrderReview(caseId),
  })

export type BlockedOrderReviewDetail = Awaited<ReturnType<typeof fetchBlockedOrderReview>>

export function useBlockedOrderReview(caseId: string | null) {
  return useQuery({
    ...blockedOrderReviewQueryOptions(caseId ?? ''),
    enabled: caseId !== null,
  })
}

export function extractBlockedOrderErrorType(error: unknown): string | null {
  const issues = (error as { issues?: { message?: string }[] } | undefined)?.issues
  const message = issues?.[0]?.message
  return isBlockedOrderErrorType(message) ? message : null
}

async function fetchBlockedOrderResolutionSnapshot(caseId: string, signal?: AbortSignal) {
  const res = await getApi().credit['blocked-orders'].reviews[':id']['resolution-snapshot'].$get(
    { param: { id: caseId } },
    { init: { signal } },
  )
  return await res.json()
}

export type BlockedOrderResolutionSnapshot = NonNullable<
  Awaited<ReturnType<typeof fetchBlockedOrderResolutionSnapshot>>
>

/**
 * The account as it stood when the order was released or rejected. Only fetched for a resolved
 * order — one still in its chain has no snapshot, and the request could only ever answer null.
 */
export function useBlockedOrderResolutionSnapshot(caseId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['blockedOrderResolutionSnapshot', caseId],
    queryFn: ({ signal }) => fetchBlockedOrderResolutionSnapshot(caseId, signal),
    enabled,
    // Written once, never changed.
    staleTime: Infinity,
  })
}

/**
 * The exception-routed reviewer handing the record to the CEB band it would otherwise have had
 * (CT-33). Rare and deliberate, so it gets its own mutation rather than a flag on the decision.
 */
export function useReturnBlockedOrderToChain(caseId: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation('credit')

  return useMutation({
    mutationFn: async (input: { comment: string }) => {
      const res = await getApi().credit['blocked-orders'].reviews[':id']['return-to-chain'].$post({
        param: { id: caseId },
        json: input,
      })
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blockedOrderReview', caseId] })
      queryClient.invalidateQueries({ queryKey: ['credit', 'inbox'] })
      toast.success(t('blockedOrders.toasts.returnedToChain'))
    },
    onError: (error) => {
      const errorType = extractBlockedOrderErrorType(error)
      toast.error(errorType ? t(`clr.errors.${errorType}`) : t('clr.toasts.error'))
    },
  })
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// The cockpit list (CT-30), over GET /credit/blocked-orders.
// ─────────────────────────────────────────────────────────────────────────────────────────────

// Deep links (emails, hand-typed URLs) can carry a scalar where the app serializes an array —
// normalize instead of crashing. Same guard as the CLR list.
const toStringArray = (value: unknown) =>
  value === undefined ? undefined : (Array.isArray(value) ? value : [value]).map(String)

/**
 * What one cockpit row is. `decision` — the default — is one row per group: a payer's orders
 * awaiting review, those awaiting approval, or the orders one decision released or rejected. `none`
 * is one row per order. Listed in the order the select shows them. Adding a dimension is a value
 * here, a grouped endpoint, and a table for its row.
 */
export const BLOCKED_ORDER_COCKPIT_GROUP_BYS = ['decision', 'none'] as const
export const DEFAULT_BLOCKED_ORDER_COCKPIT_GROUP_BY: BlockedOrderCockpitGroupBy = 'decision'
export type BlockedOrderCockpitGroupBy = (typeof BLOCKED_ORDER_COCKPIT_GROUP_BYS)[number]

export const blockedOrdersSearchSchema = z.object({
  groupBy: z.enum(BLOCKED_ORDER_COCKPIT_GROUP_BYS).optional(),
  keyword: z.string().optional(),
  payerErp: z.preprocess(toStringArray, z.array(z.string()).optional()),
  soldToErp: z.preprocess(toStringArray, z.array(z.string()).optional()),
  poNumber: z.string().optional(),
  province: z.preprocess(toStringArray, z.array(z.string()).optional()),
  status: z.preprocess(toStringArray, z.array(z.enum(BLOCKED_ORDER_COCKPIT_STATUSES)).optional()),
  sapHandoff: z.preprocess(
    toStringArray,
    z.array(z.enum(BLOCKED_ORDER_SAP_HANDOFF_STATUSES)).optional(),
  ),
  doaOptionKey: z.preprocess(toStringArray, z.array(z.string()).optional()),
  caseType: z.preprocess(toStringArray, z.array(z.string()).optional()),
  createdFrom: z.coerce.date().optional(),
  createdTo: z.coerce.date().optional(),
  page: z.number().optional(),
  pageSize: z.number().optional(),
})
export type BlockedOrdersSearch = z.infer<typeof blockedOrdersSearchSchema>

type BlockedOrdersQueries = Omit<BlockedOrdersSearch, 'createdFrom' | 'createdTo' | 'groupBy'> & {
  createdFrom?: string
  createdTo?: string
  timezone: string
  page: number
  pageSize: number
}

async function getBlockedOrders(queries: BlockedOrdersQueries, signal?: AbortSignal) {
  const res = await getApi().credit['blocked-orders'].$get(
    // `stringifyValues` widens every key to optional; `timezone` is always present, so restore it.
    { query: { ...stringifyValues(queries), timezone: queries.timezone } },
    { init: { signal } },
  )
  return await res.json()
}

export type BlockedOrderListRow = Awaited<ReturnType<typeof getBlockedOrders>>['rows'][number]

function toBlockedOrdersQueries(
  search: BlockedOrdersSearch & { pageSize: number },
): BlockedOrdersQueries {
  return {
    keyword: search.keyword,
    payerErp: search.payerErp,
    soldToErp: search.soldToErp,
    poNumber: search.poNumber,
    province: search.province,
    status: search.status,
    sapHandoff: search.sapHandoff,
    doaOptionKey: search.doaOptionKey,
    caseType: search.caseType,
    createdFrom: search.createdFrom ? formatDateOnly(search.createdFrom) : undefined,
    createdTo: search.createdTo ? formatDateOnly(search.createdTo) : undefined,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    page: search.page ?? 1,
    pageSize: search.pageSize,
  }
}

export function useBlockedOrders(
  search: BlockedOrdersSearch & { pageSize: number },
  options?: { enabled?: boolean },
) {
  const queries = toBlockedOrdersQueries(search)
  return useQuery({
    queryKey: ['blockedOrders', queries],
    queryFn: ({ signal }) => getBlockedOrders(queries, signal),
    enabled: options?.enabled,
  })
}

const toDate = (value: string | null) => (value ? new Date(value) : null)

async function getBlockedOrderGroups(queries: BlockedOrdersQueries, signal?: AbortSignal) {
  const res = await getApi().credit['blocked-orders'].groups.$get(
    { query: { ...stringifyValues(queries), timezone: queries.timezone } },
    { init: { signal } },
  )
  const { groups, rowCount } = await res.json()
  return {
    rowCount,
    groups: groups.map((group) => ({
      ...group,
      cutoffAt: toDate(group.cutoffAt),
      decision: group.decision
        ? { ...group.decision, decidedAt: new Date(group.decision.decidedAt) }
        : null,
    })),
  }
}

export type BlockedOrderGroupRow = Awaited<
  ReturnType<typeof getBlockedOrderGroups>
>['groups'][number]

/**
 * The cockpit grouped by decision — a payer's orders awaiting review, those awaiting approval, and
 * each decision that released or rejected some — paged by group.
 */
export function useBlockedOrderGroups(
  search: BlockedOrdersSearch & { pageSize: number },
  options?: { enabled?: boolean },
) {
  const queries = toBlockedOrdersQueries(search)
  return useQuery({
    // Under `blockedOrders` so everything that invalidates the cockpit refreshes this view too.
    queryKey: ['blockedOrders', 'groups', queries],
    queryFn: ({ signal }) => getBlockedOrderGroups(queries, signal),
    enabled: options?.enabled,
  })
}

async function fetchBlockedOrderGroup(key: string, signal?: AbortSignal) {
  const res = await getApi().credit['blocked-orders'].groups[':key'].$get(
    { param: { key } },
    { init: { signal } },
  )
  const group = await res.json()
  return {
    ...group,
    cutoffAt: toDate(group.cutoffAt),
    decision: group.decision
      ? { ...group.decision, decidedAt: new Date(group.decision.decidedAt) }
      : null,
    orders: group.orders.map((order) => ({
      ...order,
      cutoffAt: toDate(order.cutoffAt),
      createdAtSap: toDate(order.createdAtSap),
    })),
    pendingAtDecision: group.pendingAtDecision.map((order) => ({
      ...order,
      cutoffAt: toDate(order.cutoffAt),
      createdAtSap: toDate(order.createdAtSap),
    })),
    approvals: group.approvals.map((approval) => ({
      ...approval,
      approvedAt: new Date(approval.approvedAt),
    })),
  }
}

export type BlockedOrderGroupDetail = Awaited<ReturnType<typeof fetchBlockedOrderGroup>>

/** One cockpit group's page. */
export function useBlockedOrderGroup(key: string) {
  return useQuery({
    queryKey: ['blockedOrders', 'group', key],
    queryFn: ({ signal }) => fetchBlockedOrderGroup(key, signal),
  })
}

/** Orders a payer had released or rejected together, as the order detail shows the last of them. */
export type BlockedOrderDecisionBatch = NonNullable<BlockedOrderReviewDetail['lastDecisionBatch']>

/**
 * One decision and one comment over several blocked orders. The endpoint answers per order, so a
 * batch can partly land: the toast says how many moved and how many were skipped, and a skipped
 * order stays on the payer page as it was.
 */
export function useBlockedOrderBatchDecision() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('credit')

  return useMutation({
    mutationFn: async (input: {
      /** Each order, with the band the user saw it on, so a band that moved since is refused. */
      orders: { caseId: string; doaOptionKey: string | null }[]
      action: BlockedOrderDecision
      comment: string
    }) => {
      const res = await getApi().credit['blocked-orders'].reviews.actions.$post({ json: input })
      return await res.json()
    },
    onSuccess: ({ outcomes }) => {
      queryClient.invalidateQueries({ queryKey: ['blockedOrders'] })
      queryClient.invalidateQueries({ queryKey: ['credit', 'inbox'] })
      queryClient.invalidateQueries({ queryKey: ['blockedOrderReview'] })
      queryClient.invalidateQueries({ queryKey: ['blockedOrderResolutionSnapshot'] })

      const appliedCount = outcomes.filter((outcome) => outcome.isApplied).length
      const skippedCount = outcomes.length - appliedCount
      if (appliedCount > 0) {
        toast.success(t('blockedOrders.batch.toasts.applied', { count: appliedCount }))
      }
      if (skippedCount > 0) {
        toast.warning(t('blockedOrders.batch.toasts.skipped', { count: skippedCount }))
      }
    },
    onError: () => toast.error(t('clr.toasts.error')),
  })
}

/**
 * TEMPORARY — the cockpit's "Create test orders" button, until CT-34's IDOC brings real orders.
 * The new records land in the inbox as well as the cockpit, and the payer's other open orders take
 * the entered CEB, so everything that reads blocked orders moves.
 */
export function useCreateTestBlockedOrders() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('credit')

  return useMutation({
    mutationFn: async (input: {
      count: number
      payerErp: string
      province: ProvinceCode
      amount: number
      variance: number
      ceb: number
    }) => {
      const res = await getApi().credit['blocked-orders']['test-orders'].$post({ json: input })
      return await res.json()
    },
    onSuccess: ({ createdOrders }) => {
      queryClient.invalidateQueries({ queryKey: ['blockedOrders'] })
      queryClient.invalidateQueries({ queryKey: ['credit', 'inbox'] })
      queryClient.invalidateQueries({ queryKey: ['blockedOrderReview'] })
      toast.success(t('blockedOrders.testOrders.created', { count: createdOrders }))
    },
    onError: () => toast.error(t('clr.toasts.error')),
  })
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// The caller's source: SAP's live cases, or the cockpit's test orders. Per user.
// ─────────────────────────────────────────────────────────────────────────────────────────────

export const blockedOrderSourceQueryKey = ['blockedOrders', 'source'] as const

/** Which source the viewer works against, and whether this environment can serve it. */
/**
 * The cockpit's banner: hand-offs to SAP that need a look. Polled, since the worker moves them
 * every few minutes and the person watching the cockpit should not have to refresh.
 */
export function useBlockedOrderSapHandoffAlerts(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['blockedOrders', 'sapHandoffAlerts'],
    queryFn: async ({ signal }) => {
      const res = await getApi().credit['blocked-orders']['sap-handoff-alerts'].$get(
        {},
        { init: { signal } },
      )
      const alerts = await res.json()
      return { ...alerts, oldestStaleDecidedAt: toDate(alerts.oldestStaleDecidedAt) }
    },
    refetchInterval: 60_000,
    enabled: options?.enabled,
  })
}

export function useBlockedOrderSource() {
  return useQuery({
    queryKey: blockedOrderSourceQueryKey,
    queryFn: async ({ signal }) => {
      const res = await getApi().credit['blocked-orders'].source.$get({}, { init: { signal } })
      return await res.json()
    },
  })
}

/**
 * Switches the viewer's source. Everything that reads blocked orders reads it from the server per
 * request, so the whole cache is dropped rather than keyed by source: nothing from one source may
 * linger on screen under the other.
 */
export function useSetBlockedOrderSource() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('credit')

  return useMutation({
    mutationFn: async (source: BlockedOrderSource) => {
      const res = await getApi().credit['blocked-orders'].source.$put({ json: { source } })
      return await res.json()
    },
    onSuccess: (state) => {
      queryClient.setQueryData(blockedOrderSourceQueryKey, state)
      queryClient.removeQueries({
        predicate: ({ queryKey }) => queryKey[0] === 'blockedOrders' && queryKey[1] !== 'source',
      })
      for (const key of ['blockedOrderReview', 'blockedOrderResolutionSnapshot']) {
        queryClient.removeQueries({ queryKey: [key] })
      }
      queryClient.invalidateQueries({ queryKey: ['credit', 'inbox'] })
      toast.success(t(`blockedOrders.source.switched.${state.source}`))
    },
    onError: () => toast.error(t('clr.toasts.error')),
  })
}
